import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { Mail, MapPin, Target } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout.jsx';
import CRUDTable from '../../components/crud/CRUDTable';
import CRUDModal from '../../components/crud/CRUDModal';
import DeleteConfirmModal from '../../components/crud/DeleteConfirmModal';

// GraphQL Queries y Mutations
const GET_DESTINATIONS = gql`
  query GetDestinations {
    destinations {
      id
      destination_name
      email
      shipments {
        manifest
      }
    }
  }
`;

const CREATE_DESTINATION = gql`
  mutation CreateDestination($destination_name: String!, $email: String) {
    createDestination(destination_name: $destination_name, email: $email) {
      id
      destination_name
      email
      shipments {
        manifest
      }
    }
  }
`;

const UPDATE_DESTINATION = gql`
  mutation UpdateDestination($id: UUID!, $destination_name: String!, $email: String) {
    updateDestination(id: $id, destination_name: $destination_name, email: $email) {
      id
      destination_name
      email
      shipments {
        manifest
      }
    }
  }
`;

const DELETE_DESTINATION = gql`
  mutation DeleteDestination($id: UUID!) {
    deleteDestination(id: $id)
  }
`;

const DestinationsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Queries
  const { data, loading, error } = useQuery(GET_DESTINATIONS);

  // Mutations
  const [createDestination, { loading: createLoading }] = useMutation(CREATE_DESTINATION, {
    onCompleted: () => {
      toast.success('Destino creado exitosamente');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_DESTINATIONS }]
  });

  const [updateDestination, { loading: updateLoading }] = useMutation(UPDATE_DESTINATION, {
    onCompleted: () => {
      toast.success('Destino actualizado exitosamente');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_DESTINATIONS }]
  });

  const [deleteDestination, { loading: deleteLoading }] = useMutation(DELETE_DESTINATION, {
    onCompleted: () => {
      toast.success('Destino eliminado exitosamente');
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    refetchQueries: [{ query: GET_DESTINATIONS }]
  });

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'destination_name',
      label: 'Nombre del Destino',
      sortable: true,
      render: (name) => (
        <div className="flex items-center">
          <Target className="w-4 h-4 text-gray-500 mr-2" />
          <span className="font-medium">{name}</span>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email',
      render: (email) => (
        email ? (
          <div className="flex items-center">
            <Mail className="w-4 h-4 text-gray-500 mr-2" />
            <a href={`mailto:${email}`} className="text-blue-600 hover:text-blue-800">
              {email}
            </a>
          </div>
        ) : (
          <span className="text-gray-500">Sin email</span>
        )
      ),
    },
    {
      key: 'shipments',
      label: 'Envíos',
      render: (shipments) => (
        <div className="flex items-center">
          <span className={`px-2 py-1 text-xs rounded-full ${
            shipments.length > 0 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-600'
          }`}>
            {shipments.length} envío{shipments.length !== 1 ? 's' : ''}
          </span>
        </div>
      ),
    },
  ];

  // Configuración de campos para el modal
  const formFields = [
    {
      name: 'destination_name',
      label: 'Nombre del Destino',
      type: 'text',
      required: true,
      placeholder: 'Ingrese el nombre del destino',
      minLength: 2,
      maxLength: 100,
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: false,
      placeholder: 'Ingrese el email (opcional)',
      maxLength: 255,
    },
  ];

  // Handlers
  const handleAdd = () => {
    setSelectedDestination(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEdit = (destination) => {
    setSelectedDestination(destination);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = (destination) => {
    setSelectedDestination(destination);
    setIsDeleteModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (isEditing && selectedDestination) {
        await updateDestination({
          variables: {
            id: selectedDestination.id,
            destination_name: formData.destination_name,
            email: formData.email || null,
          },
        });
      } else {
        await createDestination({
          variables: {
            destination_name: formData.destination_name,
            email: formData.email || null,
          },
        });
      }
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDestination({
        variables: {
          id: selectedDestination.id,
        },
      });
    } catch (error) {
      // Error handled by mutation onError
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDestination(null);
    setIsEditing(false);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedDestination(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error al cargar los destinos: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Gestión de Destinos
          </h1>
          <p className="text-gray-600">
            Administra los destinos disponibles para los envíos del sistema
          </p>
        </div>

        <CRUDTable
          data={data?.destinations || []}
          columns={columns}
          isLoading={loading}
          searchPlaceholder="Buscar destinos..."
          searchFields={['destination_name', 'email']}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          addButtonText="Agregar Destino"
          emptyMessage="No hay destinos disponibles"
        />

        <CRUDModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          title={isEditing ? 'Editar Destino' : 'Agregar Destino'}
          initialData={selectedDestination || {}}
          fields={formFields}
          isLoading={createLoading || updateLoading}
          isEditing={isEditing}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Eliminar Destino"
          message="¿Estás seguro de que deseas eliminar este destino?"
          itemName={selectedDestination?.destination_name}
          isLoading={deleteLoading}
          isDangerous={selectedDestination?.shipments?.length > 0}
        />
      </div>
    </DashboardLayout>
  );
};

export default DestinationsPage;